import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthenticatedApi } from "./useAuthenticatedApi";
import { makeHotelApi, HotelInfo } from "../api/hotelApi";
import { useUserInfo } from "./useUserInfo";
import { useMediaUpload } from "./useMediaUpload";
import type { EntityType } from "../config/uploads";

export const useHotelInfo = () => {
  const { callApi } = useAuthenticatedApi();
  const { hotelId } = useUserInfo();
  const {
    upload,
    isUploading,
    progress,
    error: uploadError,
    resetState,
  } = useMediaUpload();

  const api = useMemo(() => makeHotelApi(callApi), [callApi]);

  const [data, setData] = useState<HotelInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!hotelId) return;
    setLoading(true);
    setError(null);
    try {
      const info = await api.getHotelInfo();
      setData(info);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch hotel info")
      );
    } finally {
      setLoading(false);
    }
  }, [api, hotelId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const update = useCallback(
    async (
      updates: Partial<
        Pick<
          HotelInfo,
          | "name"
          | "phone"
          | "email"
          | "website"
          | "timezone"
          | "logo"
          | "mapfile"
        >
      >
    ) => {
      setError(null);
      try {
        const updated = await api.updateHotelInfo(updates);
        setData(updated);
        return updated;
      } catch (err) {
        const e =
          err instanceof Error ? err : new Error("Failed to update hotel info");
        setError(e);
        throw e;
      }
    },
    [api]
  );

  const uploadAndPersist = useCallback(
    async (
      file: File,
      entityType: EntityType,
      persistField: "logo" | "mapfile"
    ) => {
      if (!data?.hotelid) throw new Error("Hotel ID missing");
      resetState();
      const result = await upload(file, entityType, data.hotelid);
      await update({ [persistField]: result.key } as Partial<HotelInfo>);
      await fetch();
      return result.key;
    },
    [data?.hotelid, upload, update, fetch, resetState]
  );

  const uploadLogo = useCallback(
    async (file: File) => {
      return uploadAndPersist(file, "hotel-logo", "logo");
    },
    [uploadAndPersist]
  );

  const uploadMap = useCallback(
    async (file: File) => {
      return uploadAndPersist(file, "hotel-map", "mapfile");
    },
    [uploadAndPersist]
  );

  return {
    data,
    loading,
    error,
    refresh: fetch,
    update,
    // upload state
    isUploading,
    progress,
    uploadError,
    // actions
    uploadLogo,
    uploadMap,
  };
};
